import { useEffect, useRef, useState } from 'react';
import {
  parsePortableBackup,
  restorePortableBackup,
  summarizePortableBackup,
  type ValidatedPortableBackup,
} from '../lib/portableBackup';
import { cycleModalTab, synchronizeModalFocus } from '../lib/modalFocus';

export function RestoreBackupControl({
  onRestoreComplete,
  buttonLabel = 'Restore Backup',
  successMessage = 'Restore complete. Your local Conscious Ritual data is ready.',
  disabled = false,
}: {
  onRestoreComplete(backup: ValidatedPortableBackup): void;
  buttonLabel?: string;
  successMessage?: string;
  disabled?: boolean;
}): JSX.Element {
  const fileInput = useRef<HTMLInputElement>(null);
  const restoreButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const focusState = useRef({ wasOpen: false });
  const [busy, setBusy] = useState<'restore' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<ValidatedPortableBackup | null>(null);
  const summary = pending ? summarizePortableBackup(pending) : null;

  useEffect(() => {
    synchronizeModalFocus(
      pending !== null,
      focusState.current,
      confirmButton.current,
      restoreButton.current,
    );
  }, [pending]);

  const closeConfirmation = (): void => {
    if (busy === null) setPending(null);
  };

  const selectBackup = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    setMessage(null);
    setPending(null);
    try {
      setPending(parsePortableBackup(await file.text()));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Backup could not be read.');
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const confirmRestore = async (): Promise<void> => {
    if (!pending) return;
    setBusy('restore');
    setError(null);
    try {
      await restorePortableBackup(pending);
      setPending(null);
      setMessage(successMessage);
      onRestoreComplete(pending);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Restore failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <button
        ref={restoreButton}
        type="button"
        className="btn btn-secondary"
        disabled={busy !== null || disabled}
        onClick={() => fileInput.current?.click()}
      >
        {buttonLabel}
      </button>
      <input
        ref={fileInput}
        className="visually-hidden"
        type="file"
        accept="application/json,.json,.cr-backup.json"
        aria-label="Select Conscious Ritual backup file"
        onChange={(event) => void selectBackup(event.target.files?.[0])}
      />
      {message ? <p role="status" className="notice">{message}</p> : null}
      {error ? <p role="alert" className="error-text">{error}</p> : null}

      {pending && summary ? (
        <div className="product-form-modal" role="presentation">
          <div
            ref={dialog}
            className="product-form-modal__dialog stack"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="restore-backup-title"
            aria-describedby="restore-backup-description"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                closeConfirmation();
                return;
              }
              if (event.key !== 'Tab') return;
              const focusable = Array.from(
                dialog.current?.querySelectorAll<HTMLElement>(
                  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ) ?? [],
              );
              cycleModalTab(event, focusable, document.activeElement as HTMLElement | null);
            }}
          >
            <h2 id="restore-backup-title" className="mt-0">Replace local data?</h2>
            <p id="restore-backup-description" className="mt-0">
              Restore will replace the Conscious Ritual data currently stored
              on this device. Export this device first if you want to keep its
              current data.
            </p>
            <dl className="settings-dl restore-backup-summary">
              <div><dt>Backup created</dt><dd>{new Date(summary.createdAt).toLocaleString()}</dd></div>
              <div><dt>Profile</dt><dd>{summary.hasProfile ? 'Included' : 'Not included'}</dd></div>
              <div><dt>Products</dt><dd>{summary.productCount}</dd></div>
              <div><dt>Product images</dt><dd>{summary.imageCount}</dd></div>
              <div><dt>Blend History</dt><dd>{summary.blendHistoryCount}</dd></div>
              <div><dt>Rituals</dt><dd>{summary.journalCount}</dd></div>
            </dl>
            <div className="row data-storage-actions">
              <button type="button" className="btn" disabled={busy !== null} onClick={closeConfirmation}>Cancel</button>
              <button ref={confirmButton} type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => void confirmRestore()}>
                {busy === 'restore' ? 'Restoring…' : 'Replace and Restore'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
